import React from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';

// NOTE: LEGAL_NAME must exactly match the entity registered with the IRS and used
// in the Twilio/TCR A2P brand registration. Update before publishing.
const LEGAL_NAME = 'Mickey & Associates, Inc. DBA Replay Museum';
const EFFECTIVE_DATE = 'August 24, 2026';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center mb-6">
        <Shield size={28} className="text-indigo-600 dark:text-indigo-400 mr-3" />
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Privacy Policy</h1>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
        Effective {EFFECTIVE_DATE}
      </p>

      <div className="prose dark:prose-invert max-w-none space-y-6 text-gray-700 dark:text-gray-300">
        <p>
          {LEGAL_NAME} (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) operates
          replaymuseum.com and the Replay Museum located at 119 East Tarpon Avenue, Tarpon
          Springs, Florida. This policy explains what information we collect, how we use it,
          and the choices you have.
        </p>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Information we collect</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>Contact details you give us</strong> — your name, email address and phone
            number when you use a form on this site, sign up for our newsletter, ask about a
            booking, or leave a message with our phone system.
          </li>
          <li>
            <strong>Messages you send us</strong> — the content of enquiries, game issue
            reports, and voicemails or messages left with our automated phone assistant.
          </li>
          <li>
            <strong>Basic technical information</strong> — standard web server data such as IP
            address, used to keep the site secure and to limit spam and abuse.
          </li>
        </ul>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">How we use it</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>To reply to you and answer your question.</li>
          <li>To confirm and manage bookings for parties, field trips and group visits.</li>
          <li>To send reminders about tournaments, leagues and events you have asked to hear about.</li>
          <li>To repair and maintain our games when an issue is reported.</li>
          <li>To send our newsletter, if you subscribed. Every newsletter has an unsubscribe link.</li>
        </ul>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Text messages (SMS)</h2>
        <p className="font-semibold text-gray-900 dark:text-white">
          We will not share, sell, or rent your mobile phone number or your SMS opt-in consent
          to any third party or affiliate for their own marketing purposes.
        </p>
        <p>
          We only text people who have asked us to. You can opt in by ticking the box on one of
          our forms, by telling our phone assistant it is alright to text you, or by signing up
          in person at the museum. Consent to receive text messages is never a condition of
          purchase or admission.
        </p>
        <p>
          Reply <strong>STOP</strong> to any message to opt out, or <strong>HELP</strong> for
          help. Message and data rates may apply. Full details are in our{' '}
          <Link to="/sms-terms" className="text-indigo-600 dark:text-indigo-400 hover:underline">
            Messaging Terms
          </Link>.
        </p>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Who we share information with</h2>
        <p>
          We do not sell your personal information. We share it only with service providers who
          help us operate — for example our website host, our database provider, our email
          delivery service, and our telephone and messaging provider — and only so they can
          perform that work for us. We may also disclose information if the law requires it.
        </p>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">How long we keep it</h2>
        <p>
          We keep contact details and messages for as long as needed to answer you and to keep
          records of bookings and repairs. You can ask us to delete your information at any time.
        </p>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Your choices</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>Unsubscribe from the newsletter using the link in any newsletter.</li>
          <li>Reply STOP to any text message to stop receiving texts.</li>
          <li>Email us to ask what we hold about you, or to have it corrected or deleted.</li>
        </ul>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Children</h2>
        <p>
          Our website is not directed at children under 13 and we do not knowingly collect their
          personal information. Bookings for young visitors are made by a parent, guardian or
          school.
        </p>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Changes</h2>
        <p>
          If we change this policy we will update the effective date above and post the new
          version on this page.
        </p>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Contact us</h2>
        <p>
          {LEGAL_NAME}<br />
          119 East Tarpon Avenue, Tarpon Springs, FL 34689<br />
          <a href="tel:+17279395093" className="text-indigo-600 dark:text-indigo-400 hover:underline">(727) 939-5093</a><br />
          <a href="mailto:play@replaymuseum.com" className="text-indigo-600 dark:text-indigo-400 hover:underline">play@replaymuseum.com</a>
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
